import { createContext, createRef, Component, useContext } from "react";
import ReactModal from "react-modal";
import { injectIntl, defineMessages } from "react-intl";
import classNames from "classnames";
import { uniqueId, omit, find } from "lodash";

import Alert from "../Alerts/Alert";
import ModalBreadcrumbs from "./ModalBreadcrumbs";
import withModalManager from "./withModalManager";
import PushableContainer from "../Utils/PushableContainer";
import { Desktop, NonDesktop } from "../Utils/ResponsiveQueries";
import { withNavigateTo } from "../Routes/RoutesContext";
import Button from "../Buttons/Button";

export const ModalContext = createContext({});

export function withModalContext(WrappedComponent) {
  return function WithModalContext(props) {
    const modalContext = useContext(ModalContext);
    return <WrappedComponent {...props} {...modalContext} />;
  };
}

export const useModalContext = () => {
  return useContext(ModalContext);
};

class Modal extends Component {
  static defaultProps = {
    shouldCloseOnOverlayClick: true,
    hideCloseButton: false,
    displayTitle: true
  };

  constructor(props) {
    super(props);
    this.close = this.close.bind(this);
    this.onModalInnerPointerDown = this.onModalInnerPointerDown.bind(this);
    this.onModalInnerClick = this.onModalInnerClick.bind(this);
    this.setContentRef = this.setContentRef.bind(this);

    const { setModalTitle, title } = this.props;
    setModalTitle(title);

    this.contentRef = createRef();
  }

  get modalProps() {
    const props = omit(this.props, [
      "breadcrumbs",
      "hideCloseButton",
      "title",
      "className",
      "overlayClassName",
      "isOpen",
      "onMobileBack"
    ]);

    return { ...props };
  }

  get className() {
    return classNames(["modal-wrapper", this.props.className]);
  }

  get portalClassName() {
    return classNames([
      "modal-portal",
      {
        "modal-portal-hidden": !this.props.isOpen
      }
    ]);
  }

  get overlayClassName() {
    return classNames([
      "modal-overlay",
      "bg-primary",
      "bg-primary-80-desktop",
      this.props.overlayClassName
    ]);
  }

  get fullBreadcrumbsPath() {
    // return this.props.getFullBreadcrumbsPath(this);
    return [];
  }

  get parentBreadcrumbUrl() {
    const path = [...this.fullBreadcrumbsPath].reverse();
    const parentBreadcrumb = find(path, breadcrumb => breadcrumb.url);

    if (!parentBreadcrumb) return null;

    return parentBreadcrumb.url;
  }

  get breadcrumbsComponent() {
    return (
      <ModalBreadcrumbs
        breadcrumbs={this.fullBreadcrumbsPath}
        title={this.props.title}
        closeModal={this.close}
        onMobileBack={this.props.onMobileBack}
      />
    );
  }

  get title() {
    if (!this.props.title || !this.props.displayTitle) return null;

    return (
      <Desktop>
        <h2 className="modal-title box margin-bottom-xs text-title-page">
          {this.props.title}
        </h2>
      </Desktop>
    );
  }

  get labels() {
    const { formatMessage } = this.props.intl;

    return {
      modalClose: formatMessage(intlMessages.modal)
    };
  }

  get closeButton() {
    const { hideCloseButton } = this.props;

    if (hideCloseButton) return null;

    return (
      <>
        <Button
          type="button"
          className="modal-close text-primary-inverse"
          onClick={this.close}
          autoFocus={true}
          iconClassName="icon-close"
          screenReaderLabel={this.labels.modalClose}
        />
      </>
    );
  }

  get ariaHideApp() {
    return !!this.props.appContainer;
  }

  get id() {
    return this.props.id || uniqueId("modal");
  }

  close() {
    const { onRequestClose, lastNonModalLocation, navigateTo } = this.props;

    const closeModal = () => {
      const url =
        (lastNonModalLocation && lastNonModalLocation.pathname) ||
        this.parentBreadcrumbUrl ||
        this.props.findClosestMatchingParentLocation();

      navigateTo(url);
    };

    if (onRequestClose) {
      onRequestClose(closeModal);
    } else {
      closeModal();
    }
  }

  // Browsers dispatch the click event to the closest common ancestor of the pressed and
  // released elements. So when the user starts an interaction inside the modal and
  // releases the pointer over the overlay (e.g. selecting a field's text, or dragging a
  // slider thumb outside the modal), the click would be seen as an overlay click and
  // close the modal unintentionally. We only close the modal when the interaction also
  // started on the overlay itself.
  onModalInnerPointerDown(e) {
    this.overlayPressed = e.target === e.currentTarget;
  }

  onModalInnerClick(e) {
    if (
      this.props.shouldCloseOnOverlayClick &&
      this.overlayPressed &&
      e.target === e.currentTarget
    ) {
      this.close();
    }
  }

  setContentRef(element) {
    this.contentRef.current = element;
  }

  render() {
    return (
      <ModalContext.Provider
        value={{
          closeModal: this.close,
          contentRef: this.contentRef
        }}
      >
        <ReactModal
          portalClassName={this.portalClassName}
          overlayClassName={this.overlayClassName}
          className={this.className}
          id={this.id}
          bodyOpenClassName="modal-body-open"
          onRequestClose={this.close}
          appElement={this.props.appContainer}
          ariaHideApp={this.ariaHideApp}
          isOpen={true}
          contentLabel={this.props.title}
          contentRef={this.setContentRef}
          {...this.modalProps}
        >
          <Alert />

          <PushableContainer>
            <div
              className="modal-inner-wrapper"
              onPointerDown={this.onModalInnerPointerDown}
              onClick={this.onModalInnerClick}
            >
              <div className="modal-header">
                <NonDesktop>
                  <PushableContainer />
                </NonDesktop>

                <div className="bg-primary-mobile position-relative">
                  {this.breadcrumbsComponent}
                  {this.closeButton}
                </div>
              </div>

              <div className="modal-inner">
                {this.title}

                <div className="modal-content">{this.props.children}</div>
              </div>
            </div>
          </PushableContainer>
        </ReactModal>
      </ModalContext.Provider>
    );
  }
}

export default withModalManager(withNavigateTo(injectIntl(Modal)));

const intlMessages = defineMessages({
  modal: {
    id: "modal.closeModal",
    defaultMessage: "Fermeture de la fenêtre modale"
  }
});
